import { ExternalLink } from "lucide-react";
import { useScrollAnimation } from "../hooks/useScrollAnimation";

const projects = [
  {
    name: "Klas Dolce e Salato",
    url: "https://www.klasdolcesalato.it/",
    description: "Pasticceria artigianale. Sito elegante, veloce e ottimizzato per farsi trovare dai golosi della zona.",
    image: "https://images.unsplash.com/photo-1558961363-fa8fdf82db35?auto=format&fm=webp&q=80",
  },
  {
    name: "Big Gimmy Integratori",
    url: "https://www.biggimmyintegratori.it",
    description: "E-commerce fitness. Design accattivante, checkout fluido e posizionamento Google curato nel dettaglio.",
    image: "https://images.unsplash.com/photo-1593095948071-474c5cc2989d?auto=format&fm=webp&q=80",
  },
];

function AnimatedCard({ children, delay = 0 }) {
  const { ref, isVisible } = useScrollAnimation(0.15);
  
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${
        isVisible 
          ? "opacity-100 translate-y-0" 
          : "opacity-0 translate-y-8"
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

export default function PortfolioSection() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation(0.2);

  return (
    <section
      data-testid="portfolio-section"
      id="lavori"
      className="section-padding bg-blue-600"
    >
      <div className="container-custom">
        {/* Section Header */}
        <div 
          ref={headerRef}
          className={`text-center max-w-2xl mx-auto mb-12 transition-all duration-700 ${
            headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <p className="text-blue-200 text-xs tracking-widest uppercase font-semibold mb-4">
            I NOSTRI LAVORI
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 font-['Sora']">
            Non ti raccontiamo cosa sappiamo fare.{" "}
            <span className="text-blue-200">Te lo mostriamo.</span>
          </h2>
          <p className="text-blue-100">
            Ecco alcuni siti che abbiamo creato: veloci, belli e già primi su Google.
          </p>
        </div>

        {/* Projects Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {projects.map((project, index) => (
            <AnimatedCard key={project.name} delay={index * 150}>
              <a
                href={project.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`portfolio-item-${index}`}
                data-umami-event="apri-progetto"
                data-umami-event-sito={project.name}
                className="group block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
              >
                {/* Image */}
                <div className="relative overflow-hidden aspect-video bg-slate-100">
                  <img
                    src={`${project.image}&w=600`}
                    srcSet={`${project.image}&w=400 400w, ${project.image}&w=800 800w, ${project.image}&w=1200 1200w`}
                    sizes="(max-width: 768px) 100vw, 50vw"
                    alt={`Screenshot del sito web ${project.name} realizzato da ALRender`}
                    loading={index === 0 ? "eager" : "lazy"}
                    fetchPriority={index === 0 ? "high" : "auto"}
                    decoding="async"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-xl font-bold text-slate-900 font-['Sora'] group-hover:text-blue-600 transition-colors">
                      {project.name}
                    </h3>
                    <ExternalLink className="w-5 h-5 text-slate-400 group-hover:text-blue-600 transition-colors flex-shrink-0" />
                  </div>
                  <p className="text-slate-600 text-sm">
                    {project.description}
                  </p>
                </div>
              </a>
            </AnimatedCard>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-10 text-center">
          <AnimatedCard delay={300}>
            <button
              data-testid="portfolio-cta"
              data-umami-event="richiedi-preventivo"
              data-umami-event-sezione="portfolio"
              onClick={() => {
                const element = document.querySelector("#contatto");
                if (element) element.scrollIntoView({ behavior: "smooth" });
              }}
              className="bg-white text-blue-600 font-semibold px-6 py-3 rounded-lg hover:bg-blue-50 transition-colors inline-flex items-center gap-2 shadow-lg"
            >
              Richiedi un preventivo gratuito
              <ExternalLink className="w-4 h-4" />
            </button>
          </AnimatedCard>
        </div>
      </div>
    </section>
  );
}
